import { updatePageLoadingApi } from '@slice/select.slice';
import AdminLogApi from 'apis/api/AdminLog';
import OrderManagerApi from 'apis/api/OrderManager';
import moment from 'moment';
import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';

export const useOrderDebtLogic = (
  form: any,
  listCSKH: string[],
  dataDebtOrder: any,
  netAmountForDebt: number,
  options: { autoFetch?: boolean } = {}
) => {
  const processOrderData = useCallback(
    (rawOrders: any[]) => {
      const groupedOrders: any[] = [];
      const invoiceMap: Record<string, any> = {};

      rawOrders.forEach((o: any) => {
        if (o.loai === 'Hóa Đơn') {
          if (!invoiceMap[o.maDonHang]) {
            invoiceMap[o.maDonHang] = {
              ...o,
              tienNo: 0,
              thuVat: 0,
              thuChenhLech: 0,
              goiCuoc: 0,
              orderType: o.loai,
              isGroup: true,
              childItems: []
            };
            groupedOrders.push(invoiceMap[o.maDonHang]);
          }
          invoiceMap[o.maDonHang].tienNo += (o.thuVat || 0) + (o.thuChenhLech || 0);
          invoiceMap[o.maDonHang].thuVat += o.thuVat || 0;
          invoiceMap[o.maDonHang].thuChenhLech += o.thuChenhLech || 0;
          invoiceMap[o.maDonHang].goiCuoc += o.goiCuoc || 0;
          invoiceMap[o.maDonHang].childItems.push(o);
        } else {
          groupedOrders.push({ ...o, isGroup: false, orderType: o.loai });
        }
      });

      // Tính toán checked dựa trên hạn mức
      let runningTotal = 0;
      return groupedOrders.map((o: any) => {
        const debtAmount = (o.tienNo || 0) + (o.goiCuoc || 0);
        let shouldCheck = false;
        if (netAmountForDebt === -1) shouldCheck = true;
        else if (netAmountForDebt > 0 && runningTotal + debtAmount <= netAmountForDebt) {
          shouldCheck = true;
          runningTotal += debtAmount;
        }
        return {
          ...o,
          checked: shouldCheck,
          originalTotal: o.tienNo,
          ngay: o.ngay ? moment(o.ngay).format('DD/MM/YYYY') : '-'
        };
      });
    },
    [netAmountForDebt] // Cần đưa dependency vào
  );

  const mergeWithServerData = useCallback((apiOrders: any[], serverCheckedCodes: string[]) => {
    if (!serverCheckedCodes || serverCheckedCodes.length === 0) {
      return apiOrders; // Không có server data → giữ nguyên logic processOrderData
    }

    const serverSet = new Set(serverCheckedCodes);

    return apiOrders.map(order => ({
      ...order,
      // Server đã chọn đơn nào thì force checked: true, bỏ qua netAmountForDebt
      checked: serverSet.has(order.maDonHang) ? true : false
      // Các đơn KHÔNG nằm trong server list → checked: false
      // dù netAmountForDebt có đủ hay không
    }));
  }, []);

  const onSaleChange = useCallback(
    async (vals?: string[], serverCheckedCodes?: string[], serverItems?: any[]) => {
      // 1. Chuẩn hóa giá trị
      let finalVals = vals.includes('') ? (vals[vals.length - 1] === '' ? [''] : vals.filter(v => v !== '')) : vals;
      if (finalVals.length === 0) finalVals = [''];

      // 2. Cập nhật state list nhân viên vào form
      const currentItems = form.getFieldValue('ThuNoItems') || [{}];
      currentItems[0] = { ...currentItems[0], listKinhDoanh: finalVals };
      form.setFieldsValue({ ThuNoItems: [...currentItems] });

      // 3. Gọi API
      const phone = dataDebtOrder?.phoneList?.[0]?.phone;
      if (!phone) return;

      const apiParam = finalVals.includes('GROUP_CSKH')
        ? finalVals.filter(v => v !== 'GROUP_CSKH').concat(listCSKH)
        : finalVals;

      try {
        const res = await OrderManagerApi.LayDanhSachDonNo({
          customerPhone: phone,
          saleNames: apiParam.includes('') ? [] : apiParam
        });

        if (res.data && !res.data.error) {
          const apiData = res.data.data || [];

          let finalItems: any[];

          if (serverCheckedCodes && serverCheckedCodes.length > 0 && serverItems) {
            const apiData = res.data.data || [];

            // Lấy tất cả maDonHang từ API (bao gồm cả childItems nếu có)
            const apiCodesSet = new Set(
              apiData.flatMap((o: any) =>
                o.loai === 'Hóa Đơn' && o.childItems?.length > 0
                  ? o.childItems.map((c: any) => c.maDonHang)
                  : [o.maDonHang]
              )
            );

            // Lấy maDonHang đại diện của từng serverItem để so sánh
            const getMaDonHangRepresent = (item: any): string[] => {
              if (item.isGroup && item.childItems?.length > 0) {
                return item.childItems.map((c: any) => c.maDonHang);
              }
              return [item.maDonHang];
            };

            // Đơn server mà API không trả về bất kỳ child nào → append nguyên cấu trúc
            const missingFromApi = serverItems.filter((serverItem: any) => {
              const codes = getMaDonHangRepresent(serverItem);
              return codes.every(code => !apiCodesSet.has(code));
            });

            // processOrderData chỉ chạy trên apiData (đã có loai đầy đủ)
            const processedApi = processOrderData(apiData);

            // Append missingFromApi sau (giữ nguyên cấu trúc isGroup/childItems từ server)
            // Chỉ cần reset checked về đúng state
            const processedMissing = missingFromApi.map((item: any) => ({
              ...item,
              checked: true // Đơn từ server đã chọn → luôn true
            }));

            const serverSet = new Set(serverCheckedCodes);
            const processedApiWithCheck = processedApi.map((order: any) => ({
              ...order,
              checked: serverSet.has(order.maDonHang) ? true : false
            }));

            finalItems = [...processedApiWithCheck, ...processedMissing];
          } else {
            // Không có server data → chạy bình thường với netAmountForDebt
            finalItems = processOrderData(apiData);
          }

          const currentItems = form.getFieldValue('ThuNoItems') || [{}];
          currentItems[0] = { ...currentItems[0], listKinhDoanh: finalVals };
          currentItems[0].inDebtOrderCodes = finalItems;
          form.setFieldsValue({ ThuNoItems: [...currentItems] });
        }
      } catch (e) {
        console.error('Lỗi lấy danh sách đơn nợ', e);
      }
    },
    [dataDebtOrder, listCSKH, processOrderData, form]
  );

  return { onSaleChange };
};

export const useAdminConfig = (configKey: string) => {
  const [configValue, setConfigValue] = useState<number>(0);
  const [newValue, setNewValue] = useState<any>([]);
  const dispatch = useDispatch();

  const fetchConfig = async () => {
    // Bật loading
    dispatch(updatePageLoadingApi(true));

    try {
      if (configKey === 'UserApproveDebtOrder' || configKey === 'EditInVoice') {
        const res = await AdminLogApi.GetConfigWithKey(configKey);

        if (!res.data.error) {
          // Ép kiểu về Number như logic gốc của bạn
          // const value = Number(res.data.data.data);
          // const newValue = res.data.data.data;
          // setConfigValue(value);
          // setNewValue(newValue);
          const allowedUsers = res.data.data.data ? res.data.data.data.split(',') : [];
          setNewValue(allowedUsers);
        }
      } else {
        const res = await AdminLogApi.GetConfigWithKey(configKey);

        if (!res.data.error) {
          // Ép kiểu về Number như logic gốc của bạn
          const value = Number(res.data.data.data);
          setConfigValue(value);
        }
      }
    } catch (error) {
      console.error(`Error fetching config for ${configKey}:`, error);
    } finally {
      // Tắt loading (đặt trong finally để luôn chạy dù thành công hay lỗi)
      dispatch(updatePageLoadingApi(false));
    }
  };

  useEffect(() => {
    if (configKey) {
      fetchConfig();
    }
  }, [configKey]);

  // Trả về giá trị và hàm refetch nếu cần gọi lại
  return { configValue, newValue, refetch: fetchConfig };
};
